import { PRINTABLE_PROVIDER_OPTIONS } from '@/Ship/Contracts/PrintableProvider'

import type { PrintableProviderOption } from '@/Ship/Contracts/PrintableProvider'

/** Рядок довідки: змінна так, як її пишуть у шаблоні, і що вона дає. */
export interface TemplateSyntaxExample {
    macro: string
    meaning: string
}

/**
 * Провайдер типу документа з форми — опція або `null`, якщо його ще не
 * обрано чи значення невідоме.
 */
export const printableProviderOf = (value: unknown): PrintableProviderOption | null =>
    PRINTABLE_PROVIDER_OPTIONS.find((option) => option.value === value) ?? null

/**
 * Приклади синтаксису змінних шаблону .docx (вичитка підказок 2026-10-14,
 * №24) — кожен звірено з беком:
 *
 *  - префікс власника — значення провайдера: `client.*` є лише в каталозі
 *    клієнта, `carrier.*` — лише перевізника (`VariableCatalog::CLIENT`,
 *    `::CARRIER`); `fullName` і `fullName~transliteration` є в обох;
 *  - у заявки `fullName` немає — приклади з `VariableCatalog::APPLICATION`:
 *    адреси (`PrintableFormatter::expandedList`, кожна з нового рядка) і
 *    сума словами (`PrintableFormatter::money`); ключі сторін заявки
 *    (`client.*`, `carrier.*`) і договору називає сам компонент;
 *  - `date~formatted` — спільна змінна обох провайдерів
 *    (`VariableCatalog::COMMON`), «7 жовтня 2026» — `PrintableFormatter::dateWords`;
 *  - печатка — `VariableCatalog::STAMP` з позиційними параметрами PhpWord
 *    «ширина:висота:пропорції» (`TemplateProcessor::getImageArgs`).
 *
 * Без провайдера прикладів власника немає: префікс тоді невідомий.
 */
const ownerExamples = (provider: PrintableProviderOption | null): TemplateSyntaxExample[] => {
    if (provider === null) return []

    if (provider.value === 'application') {
        return [
            { macro: '${application.addressLoading}', meaning: 'адреси завантаження, кожна з нового рядка' },
            { macro: '${application.addressLoading.1}', meaning: 'перша адреса завантаження (є .1…10)' },
            { macro: '${application.costCarrier~words}', meaning: 'вартість для перевізника словами' },
            { macro: '${contractNumber}', meaning: 'номер договору з форми генерації' },
        ]
    }

    return [
        { macro: `\${${provider.value}.fullName}`, meaning: 'повна назва' },
        { macro: `\${${provider.value}.fullName~transliteration}`, meaning: 'те саме латиницею' },
    ]
}

export const templateSyntaxExamples = (provider: PrintableProviderOption | null): TemplateSyntaxExample[] => [
    ...ownerExamples(provider),
    { macro: '${date~formatted}', meaning: 'дата документа словами, наприклад «7 жовтня 2026»' },
    { macro: '${legalEntity.stamp:180:180:true}', meaning: 'печатка юрособи: ширина, висота, зберегти пропорції' },
]
